import { GridColDef } from '@mui/x-data-grid';
import { Campaign } from '@libs/models';
import { CampaignPPCString, CampaignPartner, CampaignStatus } from '@libs/types';
import { CampaignPartnerChip, CampaignStatusChip } from '@carcareers/components';
import { DataGridLink } from '@carcareers/components';
import { routes } from '@carcareers/utils';

export const CAMPAIGN_INDEX_COLUMNS: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 90 },
  {
    field: 'postId',
    headerName: 'Post ID',
    width: 90,
    renderCell: (params) =>
      params.row.post?.id ? (
        <DataGridLink
          link={`/${routes.ADMIN_HOME}/${routes.ADMIN_POSTS_VIEW.replace(':id', params.row.post.id.toString())}`}
          content={params.row.post.id}
        />
      ) : (
        <span />
      ),
  },
  {
    field: 'jobId',
    headerName: 'Job ID',
    width: 90,
    renderCell: (params) =>
      params.row.post?.job?.id ? (
        <DataGridLink
          link={`/${routes.ADMIN_HOME}/${routes.ADMIN_JOBS_VIEW.replace(':id', params.row.post.job.id.toString())}`}
          content={params.row.post.job.id}
        />
      ) : (
        <span />
      ),
  },
  {
    field: 'job',
    headerName: 'Job',
    width: 200,
    renderCell: (params) =>
      params.row.post?.id ? (
        <DataGridLink
          link={`/${routes.ADMIN_HOME}/${routes.ADMIN_POSTS_VIEW.replace(':id', params.row.post.id.toString())}`}
          content={params.row.post?.job?.title || ''}
        />
      ) : (
        <span />
      ),
  },
  {
    field: 'rooftopId',
    headerName: 'Rooftop ID',
    width: 90,
    renderCell: (params) =>
      params.row.post?.job?.rooftop?.id ? (
        <DataGridLink
          link={`/${routes.ADMIN_HOME}/${routes.ADMIN_ROOFTOPS_VIEW.replace(
            ':id',
            params.row.post.job.rooftop.id.toString(),
          )}`}
          content={params.row.post.job.rooftop.id}
        />
      ) : (
        <span />
      ),
  },
  {
    field: 'rooftop',
    headerName: 'Rooftop',
    width: 150,
    renderCell: (params) =>
      params.row.post?.job?.rooftop?.id ? (
        <DataGridLink
          link={`/${routes.ADMIN_HOME}/${routes.ADMIN_ROOFTOPS_VIEW.replace(
            ':id',
            params.row.post.job.rooftop.id.toString(),
          )}`}
          content={params.row.post.job.rooftop.name}
        />
      ) : (
        <span />
      ),
  },
  {
    field: 'employerId',
    headerName: 'Employer ID',
    width: 90,
    renderCell: (params) =>
      params.row.post?.postedBy?.id ? (
        <DataGridLink
          link={`/${routes.ADMIN_HOME}/${routes.ADMIN_EMPLOYERS_VIEW.replace(
            ':id',
            params.row.post.postedBy.id.toString(),
          )}`}
          content={params.row.post.postedBy.id}
        />
      ) : (
        <span />
      ),
  },
  {
    field: 'employer',
    headerName: 'Employer',
    width: 150,
    renderCell: (params) => {
      const firstName = params.row.post?.postedBy?.user?.firstName || '';
      const lastName = params.row.post?.postedBy?.user?.lastName || '';
      const name = firstName && lastName ? `${firstName} ${lastName}` : '';
      return params.row.post?.postedBy?.id ? (
        <DataGridLink
          link={`/${routes.ADMIN_HOME}/${routes.ADMIN_EMPLOYERS_VIEW.replace(
            ':id',
            params.row.post.postedBy.id.toString(),
          )}`}
          content={name}
        />
      ) : (
        <span />
      );
    },
  },
  {
    field: 'jobLocation',
    headerName: 'Location',
    width: 200,
    valueGetter: (_, row) => row.post?.job?.rooftop?.location || '',
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (_, row) => row.status,
    renderCell: (params) => {
      const status: CampaignStatus = params.value as CampaignStatus;
      return <CampaignStatusChip status={status} />;
    },
  },
  {
    field: 'partner',
    headerName: 'Partner',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      const partner: CampaignPartner = params.value as CampaignPartner;
      return <CampaignPartnerChip partner={partner} />;
    },
  },
  {
    field: 'ppc',
    headerName: 'Price Per Click',
    width: 150,
    valueGetter: (_, row: Campaign) =>
      row.ppc && row.partner !== CampaignPartner.APPCAST ? CampaignPPCString(row.ppc) : '-',
  },
  {
    field: 'budget',
    headerName: 'Budget',
    width: 150,
    valueGetter: (_, row) =>
      row.budget && row.partner !== CampaignPartner.APPCAST ? `$${row.budget.toLocaleString()}` : '-',
  },
  {
    field: 'applications',
    headerName: 'Applied',
    width: 150,
    valueGetter: (_, row) => row.post?.applications?.length || 0,
  },
  {
    field: 'startDate',
    headerName: 'Start',
    width: 150,
    valueGetter: (_, row) =>
      new Date(row.startDate).toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
      }) || '',
  },
  {
    field: 'endDate',
    headerName: 'End Date',
    width: 150,
    valueGetter: (_, row) =>
      new Date(row.endDate).toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
      }) || '',
  },
];

export const CAMPAIGN_INDEX_COLUMN_INITIAL_STATE = {
  id: false,
  postId: true,
  jobId: true,
  job: true,
  rooftopId: false,
  rooftop: true,
  employerId: false,
  employer: false,
  jobLocation: true,
  partner: true,
  ppc: true,
  startDate: true,
  endDate: false,
  budget: true,
  status: true,
};
